import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";
import { ui, useUi } from "@/app/ui-store";
import { Button } from "@/components/ui/button";
import { sendErrorInfo } from "@/lib/reauth";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { ConnectInstagramButton, ConnectInstagramDialog } from "./instagram-connect";

/**
 * "Instagram needs to be reconnected" + Reconnect (opens the Instagram connect explainer). Shown wherever an action
 * failed with `channel_reauth_required` or targets a `needs_reauth` account: composers, bulk bar, jobs, approvals.
 */
export function ReauthNotice({ username, className, testId = "reauth-notice" }: { username?: string | null; className?: string; testId?: string }) {
  const { t } = useT();
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn("flex flex-wrap items-start gap-2.5 rounded-xl border border-[color-mix(in_srgb,var(--warn)_45%,var(--line))] bg-warn-soft px-3 py-2.5", className)}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-ink">{t("oauth.reauth.needed")}</p>
        <p className="mt-0.5 text-[12px] text-ink-2">{username ? t("oauth.reauth.hintUser", { user: username }) : t("oauth.reauth.hint")}</p>
      </div>
      <ConnectInstagramButton size="sm" variant="primary" icon={<RefreshCw />} data-testid="reauth-reconnect">
        {t("oauth.reauth.reconnect")}
      </ConnectInstagramButton>
    </div>
  );
}

/**
 * A failed send / job explained by its stable error code (lib/reauth `sendErrorInfo`): reauth → the notice with
 * Reconnect; rate limits / provider errors / legacy "Activity task failed" → "Sending failed" + Retry (when the caller
 * can retry); everything else → its specific message, no retry. The raw error is secondary detail only.
 */
export function SendErrorNotice({
  failure, username, onRetry, retrying, notSent, className, testId = "send-error", genericMessage,
}: {
  failure: { errorCode?: string | null; error?: string | null } | unknown;
  username?: string | null;
  onRetry?: () => void;
  retrying?: boolean;
  /** Phrase it as "Not sent: …" (message bubbles, reply results). */
  notSent?: boolean;
  className?: string;
  testId?: string;
  /** Text for a failure without a typed reason (`unknown`) when "Sending failed" doesn't fit (a resync, an import…). */
  genericMessage?: string;
}) {
  const { t } = useT();
  const base = sendErrorInfo(failure);
  const info = base.code === "unknown" && genericMessage ? { ...base, message: genericMessage } : base;
  if (info.action === "reconnect") return <ReauthNotice username={username} className={className} testId={testId === "send-error" ? "reauth-notice" : testId} />;
  return (
    <div role="alert" data-testid={testId} data-code={info.code} className={cn("flex flex-wrap items-start gap-2 rounded-xl border border-[color-mix(in_srgb,var(--bad)_35%,var(--line))] bg-bad-soft px-3 py-2", className)}>
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-bad" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-ink">{notSent ? t("common.sendError.notSent", { reason: info.message }) : info.message}</p>
        {info.detail && <p className="mt-0.5 break-words text-[11.5px] text-ink-2">{info.detail}</p>}
      </div>
      {info.action === "retry" && onRetry && (
        <Button size="sm" variant="outline" icon={<RotateCcw />} loading={retrying} onClick={onRetry} data-testid="send-retry">
          {t("common.sendError.retry")}
        </Button>
      )}
    </div>
  );
}

/** One Instagram connect dialog for the whole app, opened by reauth toasts (`openReconnect()` in lib/reauth). */
export function ReconnectHost() {
  const { reconnect } = useUi();
  return <ConnectInstagramDialog open={reconnect} onOpenChange={(v) => ui.set({ reconnect: v })} />;
}
